import { useContext, memo, useEffect, useRef } from "react";
import { Accordion } from "react-bootstrap";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

import AuthContext from "common/store/AuthContext";
import MenuContext from "common/store/MenuContext";
import { TAB_ID } from "common/types/consts/Defines";

import { SideMenuItem } from "../molecules/SideMenuItem";
import { ParkTabInfo } from "common/types/ParkInfo";

let scrollPos = 0;

type Props = {
  className?: string;
  menuClicked: () => void;
};

const ILSidebar = memo((props: Props) => {
  const { menuClicked } = props;
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const authCtxt = useContext(AuthContext);
  const isAdmin = authCtxt.selectedPark?.isAdmin;
  const mapAvailable = authCtxt.selectedPark?.isMapAvailable;
  const {
    activeKeyCommon,
    setActiveKeyCommon,
    activeKeyDaily,
    setActiveKeyDaily,
    activeKeyWork,
    setActiveKeyWork,
    activeKeyOthers,
    setActiveKeyOthers,
    activeKeyMap,
    setActiveKeyMap,
    activeKeyAdmin,
    setActiveKeyAdmin,
  } = useContext(MenuContext);

  const findTab = (tabs: ParkTabInfo[] | undefined, tabId: number) => {
    return tabs?.find((tab) => tab.id === tabId);
  };

  const findTabs = (tabs: ParkTabInfo[] | undefined, tabId: number) => {
    return tabs?.filter((tab) => tab.id === tabId);
  };

  var dailyTabs = authCtxt.selectedPark?.dailyTabs;
  const staffsTabDaily = findTab(dailyTabs, TAB_ID.STAFFS);
  const visitorsTab = findTab(dailyTabs, TAB_ID.VISITORS);
  const inquiryTab = findTab(dailyTabs, TAB_ID.INQUIRY);
  const exclusiveUsesTab = findTab(dailyTabs, TAB_ID.EXCLUSIVE_USE);
  const incidentsTab = findTab(dailyTabs, TAB_ID.INCIDENTS);
  const payFacilityTab = findTab(dailyTabs, TAB_ID.PAY_FACILITY);
  const independentEventTabs = findTabs(dailyTabs, TAB_ID.PRIVATE_BIZ);
  const collaborateEventTabs = findTabs(dailyTabs, TAB_ID.PUBLIC_BIZ);
  const specialNoteTabDaily = findTab(dailyTabs, TAB_ID.SPECIAL_NOTE);
  const commentTabDaily = findTab(dailyTabs, TAB_ID.COMMENT);
  const periodEventTab = findTab(dailyTabs, TAB_ID.PERIODICAL_EVENT);

  var workTabs = authCtxt.selectedPark?.workTabs;
  const staffsTabWork = findTab(workTabs, TAB_ID.STAFFS);
  const workReportTab = findTab(workTabs, TAB_ID.WORK_REPORT);
  const patrolTab = findTab(workTabs, TAB_ID.PATROL);
  const parkWorkTab = findTab(workTabs, TAB_ID.PARK_WORK);
  const specialNoteTabWork = findTab(workTabs, TAB_ID.SPECIAL_NOTE);
  const commentTabWork = findTab(workTabs, TAB_ID.COMMENT);

  const toCategoryPath = (categoryId?: number) => {
    return categoryId !== undefined ? `/${categoryId}` : "";
  };

  useEffect(() => {
    const barDiv = rootRef.current?.parentElement;

    if (!barDiv) return;
    barDiv.scrollTo(0, scrollPos);

    const onScroll = () => {
      scrollPos = barDiv.scrollTop;
    };

    barDiv.addEventListener("scroll", onScroll);
    return () => {
      barDiv.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    const barDiv = rootRef.current?.parentElement;
    if (!barDiv) return;

    const activeMenu = barDiv.querySelector<HTMLElement>(".nav-link.active");
    if (!activeMenu) return;

    const containerTop = barDiv.scrollTop;
    const containerBottom = containerTop + barDiv.clientHeight;
    const itemTop = activeMenu.offsetTop;
    const itemBottom = itemTop + activeMenu.offsetHeight;

    if (itemTop < containerTop) {
      barDiv.scrollTo({ top: Math.max(itemTop - 16, 0), behavior: "auto" });
      return;
    }

    if (itemBottom > containerBottom) {
      barDiv.scrollTo({
        top: Math.max(itemBottom - barDiv.clientHeight + 16, 0),
        behavior: "auto",
      });
    }
  }, [location.pathname, location.search]);

  return (
    <SRoot ref={rootRef}>
      <Accordion activeKey={activeKeyCommon}>
        <SHeader onClick={() => setActiveKeyCommon(activeKeyCommon === "" ? "common" : "")}>
          共通
        </SHeader>
        <Accordion.Item eventKey="common">
          <SAccordionBody>
            <SideMenuItem
              title="ホーム"
              iconClassName="fa fas fa-home"
              to="/"
              onClick={menuClicked}
            />
            <SideMenuItem
              title="出力"
              iconClassName="fa fas fa-print"
              to="/print"
              onClick={menuClicked}
            />
          </SAccordionBody>
        </Accordion.Item>
      </Accordion>
      <Accordion activeKey={activeKeyDaily}>
        <SHeader onClick={() => setActiveKeyDaily(activeKeyDaily === "" ? "daily" : "")}>
          公園日報
        </SHeader>
        <Accordion.Item eventKey="daily">
          <SAccordionBody>
            {/* 勤務者 */}
            {staffsTabDaily && (
              <SideMenuItem
                title={staffsTabDaily.name}
                iconClassName="fas fa-user-check"
                to="/parkstaffs/daily"
                onClick={menuClicked}
              />
            )}
            {/* 利用者数 */}
            {visitorsTab && (
              <SideMenuItem
                title={visitorsTab.name}
                iconClassName="fas fa-users"
                to="/visitors"
                onClick={menuClicked}
              />
            )}
            {/* 問合せ苦情要望 */}
            {inquiryTab && (
              <SideMenuItem
                title={inquiryTab.name}
                iconClassName="fas fa-phone"
                to="/inquiry"
                onClick={menuClicked}
              />
            )}
            {/* 一時占用 */}
            {exclusiveUsesTab && (
              <SideMenuItem
                title={exclusiveUsesTab.name}
                iconClassName="fas fa-photo-film"
                to="/exclusiveuses"
                onClick={menuClicked}
              />
            )}
            {incidentsTab && (
              <SideMenuItem
                title={incidentsTab.name}
                iconClassName="fas fa-person-falling-burst"
                to="/incidents"
                onClick={menuClicked}
              />
            )}
            {/* 有料施設 */}
            {payFacilityTab && (
              <SideMenuItem
                title={payFacilityTab.name}
                iconClassName="fas fa-sack-dollar"
                to="/payfacility"
                onClick={menuClicked}
              />
            )}
            {/* 自主事業 */}
            {independentEventTabs &&
              independentEventTabs.map((x) => (
                <SideMenuItem
                  key={`prvBiz_${x.id}_${x.categoryId ?? 0}`}
                  title={x.name}
                  iconClassName="fas fa-calendar-days"
                  to={`/parkEvents/independent${toCategoryPath(x.categoryId)}`}
                  onClick={menuClicked}
                />
              ))}
            {/* 住民協働事業 */}
            {collaborateEventTabs &&
              collaborateEventTabs.map((x) => (
                <SideMenuItem
                  key={`coopBiz_${x.id}_${x.categoryId ?? 0}`}
                  title={x.name}
                  iconClassName="far fa-calendar-days"
                  to={`/parkEvents/collaborate${toCategoryPath(x.categoryId)}`}
                  onClick={menuClicked}
                />
              ))}
            {/* 特記メモ */}
            {specialNoteTabDaily && (
              <SideMenuItem
                title={specialNoteTabDaily.name}
                iconClassName="fa fa-file-pen"
                to="/specialnotes/daily"
                onClick={menuClicked}
              />
            )}
            {/* コメント */}
            {commentTabDaily && (
              <SideMenuItem
                title={commentTabDaily.name}
                iconClassName="far fa-comment-dots"
                to="/comments/daily"
                onClick={menuClicked}
              />
            )}
          </SAccordionBody>
        </Accordion.Item>
      </Accordion>

      <Accordion activeKey={activeKeyWork}>
        <SHeader onClick={() => setActiveKeyWork(activeKeyWork === "" ? "work" : "")}>
          作業日報
        </SHeader>
        <Accordion.Item eventKey="work">
          <SAccordionBody>
            {/* 勤務者（作業日報） */}
            {staffsTabWork && (
              <SideMenuItem
                title={staffsTabWork.name}
                iconClassName="fas fa-user-check"
                to="/parkstaffs/work"
                onClick={menuClicked}
              />
            )}
            {/* 巡回作業日報 */}
            {workReportTab && (
              <SideMenuItem
                title={workReportTab?.name}
                iconClassName="fas fa-walkie-talkie"
                to="/workreport"
                onClick={menuClicked}
              />
            )}
            {/* 巡回のみ */}
            {patrolTab && (
              <SideMenuItem
                title={patrolTab?.name}
                iconClassName="fas fa-road-circle-check"
                to="/workreport/patrol"
                onClick={menuClicked}
              />
            )}
            {/* 作業のみ */}
            {parkWorkTab && (
              <SideMenuItem
                title={parkWorkTab?.name}
                iconClassName="fas fa-dolly"
                to="/workreport/work"
                onClick={menuClicked}
              />
            )}
            {/* 特記メモ（作業日報） */}
            {specialNoteTabWork && (
              <SideMenuItem
                title={specialNoteTabWork?.name}
                iconClassName="fa fa-file-pen"
                to="/specialnotes/work"
                onClick={menuClicked}
              />
            )}
            {/* コメント（作業日報） */}
            {commentTabWork && (
              <SideMenuItem
                title={commentTabWork.name}
                iconClassName="fa fa-comment-dots"
                to="/comments/work"
                onClick={menuClicked}
              />
            )}
          </SAccordionBody>
        </Accordion.Item>
      </Accordion>

      {/* その他 */}
      {periodEventTab && (
        <Accordion activeKey={activeKeyOthers}>
          <SHeader onClick={() => setActiveKeyOthers(activeKeyOthers === "" ? "others" : "")}>
            その他
          </SHeader>
          <Accordion.Item eventKey="others">
            <SAccordionBody>
              <SideMenuItem
                title={periodEventTab.name}
                iconClassName="far fa-calendar-check"
                to="/parkevents/periodical"
                onClick={menuClicked}
              />
            </SAccordionBody>
          </Accordion.Item>
        </Accordion>
      )}

      {/* 地図 */}
      {mapAvailable && (
        <Accordion activeKey={activeKeyMap}>
          <SHeader onClick={() => setActiveKeyMap(activeKeyMap === "" ? "maps" : "")}>分析</SHeader>
          <Accordion.Item eventKey="maps">
            <SAccordionBody>
              <SideMenuItem
                title="問合苦情要望"
                iconClassName="fas fa-phone"
                to="/analysis/inquiry"
                onClick={menuClicked}
              />
              <SideMenuItem
                title="作業"
                iconClassName="fas fa-dolly"
                to="/analysis/work"
                onClick={menuClicked}
              />
            </SAccordionBody>
          </Accordion.Item>
        </Accordion>
      )}

      {/* 管理者 */}
      {isAdmin && (
        <Accordion activeKey={activeKeyAdmin}>
          <SHeader onClick={() => setActiveKeyAdmin(activeKeyAdmin === "" ? "settings" : "")}>
            管理者
          </SHeader>
          <Accordion.Item eventKey="settings">
            <SAccordionBody>
              <SideMenuItem
                title="帳票登録"
                iconClassName="fas fa-print"
                to="/admin/printtemplate"
                onClick={menuClicked}
              />
              <SideMenuItem
                title="勤務者設定"
                iconClassName="fas fa-user-gear"
                to="/admin/staffs"
                onClick={menuClicked}
              />
              <SideMenuItem
                title="作業計画取込"
                iconClassName="fas fa-file-import"
                to="/admin/tasks"
                onClick={menuClicked}
              />
              <SideMenuItem
                title="日報管理"
                iconClassName="fas fa-newspaper"
                to="/admin/reports"
                onClick={menuClicked}
              />
            </SAccordionBody>
          </Accordion.Item>
        </Accordion>
      )}
    </SRoot>
  );
});

export default ILSidebar;

const SRoot = styled.div`
  display: block;
  margin: 0;
  margin-top: 1px;
  width: 100%;
`;

const SHeader = styled(Accordion.Header)`
  .accordion-button {
    padding: 0.5rem 1rem;
    background-color: ${(props) => props.theme.color.menuHeader};
    color: ${(props) => props.theme.color.menuHeaderText};
    &.collapsed {
      background-color: ${(props) => props.theme.color.menuHeader};
      color: ${(props) => props.theme.color.menuHeaderText};
    }
  }
  border-radius: 1rem;
`;

const SAccordionBody = styled(Accordion.Body)`
  padding: 0;
`;
